import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Spinner } from "reactstrap";
import api from "api";
import { toast } from "react-toastify";

const DEFAULT_RECIPIENT = "gzwarehouse@yahoo.com";
const LS_KEY = "veriscan.shareBatch.lastRecipient";
const NOTE_MAX = 2000;

/**
 * Share batch codes via email modal.
 *
 * Props:
 *  - isOpen: bool
 *  - toggle: () => void
 *  - batch: the batch object (single-batch share)
 *  - batchIds?: string[] (optional override for multi-batch share)
 *  - brandName: string (for display)
 *  - orderName?: string (when sharing a whole order, used as the modal subtitle)
 */
const ShareBatchModal = ({ isOpen, toggle, batch, batchIds, brandName, orderName }) => {
    const [recipient, setRecipient] = useState(DEFAULT_RECIPIENT);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    const ids = batchIds || (batch?._id ? [batch._id] : []);

    useEffect(() => {
        if (!isOpen) return;
        // Pre-fill last used recipient, fall back to default
        try {
            const last = localStorage.getItem(LS_KEY);
            setRecipient(last || DEFAULT_RECIPIENT);
        } catch {
            setRecipient(DEFAULT_RECIPIENT);
        }
        setNote("");
    }, [isOpen]);

    const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && s.length <= 254;

    const handleSend = async () => {
        if (!isValidEmail(recipient)) {
            toast.error("Please enter a valid email address");
            return;
        }
        if (ids.length === 0) {
            toast.error("No batches to share");
            return;
        }

        setSending(true);
        try {
            const res = await api("post", "/codes/shareOrderCode", {
                batchIds: ids,
                recipient: recipient.trim(),
                note: note.trim() || undefined,
            });

            try { localStorage.setItem(LS_KEY, recipient.trim()); } catch {}

            if (res?.devSkipped) {
                toast.warn(`Recorded but no email sent (SES not configured in this environment)`);
            } else {
                const deliveryMsg = res?.delivery === "r2-link"
                    ? `Sent to ${recipient} (large file — download link expires in 90 days)`
                    : `Sent to ${recipient}`;
                toast.success(deliveryMsg);
            }
            toggle();
        } catch (err) {
            // axios interceptor already toasts on error; nothing more to do
        } finally {
            setSending(false);
        }
    };

    const productName = orderName
        || batch?.product_name
        || (ids.length > 1 ? `${ids.length} batches` : "batch");
    const printQty = Number(batch?.number_of_codes || 0);
    const generatedQty = Number(batch?.generated_code_by_increase || 0);
    const excess = generatedQty - printQty;

    return (
        <Modal isOpen={isOpen} toggle={sending ? undefined : toggle} centered>
            <ModalHeader toggle={sending ? undefined : toggle}>Share batch with factory</ModalHeader>
            <ModalBody>
                <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fb", borderRadius: 6, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, color: "#111" }}>{brandName || "—"}</div>
                    <div style={{ color: "#555", marginTop: 2 }}>{productName}</div>
                    {batch && (
                        <div style={{ color: "#666", marginTop: 6, fontSize: 12 }}>
                            Print Qty: <strong>{printQty.toLocaleString()}</strong>
                            {generatedQty > 0 && (
                                <> · Generated: <strong>{generatedQty.toLocaleString()}</strong>
                                {excess > 0 && <span style={{ color: "#888" }}> (+{excess} surplus)</span>}</>
                            )}
                        </div>
                    )}
                </div>

                <FormGroup>
                    <Label for="share-recipient" style={{ fontSize: 12, fontWeight: 600 }}>Recipient email</Label>
                    <Input
                        id="share-recipient"
                        type="email"
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                        placeholder="gzwarehouse@yahoo.com"
                        disabled={sending}
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="share-note" style={{ fontSize: 12, fontWeight: 600 }}>
                        Note <span style={{ color: "#888", fontWeight: 400 }}>(optional, {NOTE_MAX - note.length} chars left)</span>
                    </Label>
                    <Input
                        id="share-note"
                        type="textarea"
                        rows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                        placeholder="e.g. Please confirm receipt and start production Monday"
                        disabled={sending}
                    />
                </FormGroup>

                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                    The factory will receive the codes as a CSV {ids.length > 1 ? "ZIP " : ""}attachment, plus a clear print-quantity summary in the email body.
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={sending}>Cancel</Button>
                <Button color="primary" onClick={handleSend} disabled={sending}>
                    {sending ? <><Spinner size="sm" /> Sending…</> : "Send"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ShareBatchModal;
